// useDiagramDataLoader.js
import { useDispatch } from 'react-redux';
import { setPageSize, setScaleCm, setLabelFontPx, setGridGapPx, setAutosaveIntervalMinutes, replaceTemplateSettings, setHideParameterLabelsDefault, setAttributeLabelSeparator, replaceSymbolLabelOverrides } from '../redux/reducers/global/view/viewSlice';
import { fetchDiagram } from '../redux/reducers/global/diagram/diagramSlice';
import { setNodesActionCreator } from '../redux/reducers/global/node/nodeSlice';
import { setEdgesActionCreator } from '../redux/reducers/global/edge/edgeSlice';
import { PAGE_SIZE_A4, PAGE_SIZE_A3, PAGE_SIZE_ARCH_D } from './constants/pageSizeConstants';
import { alignNodesToNearestGrid } from './gridAlignment';

const VALID_PAGE_SIZES = new Set([PAGE_SIZE_A4, PAGE_SIZE_A3, PAGE_SIZE_ARCH_D]);

const useDiagramDataLoader = () => {
  const dispatch = useDispatch();

  const loadDiagramData = async ({ diagramId, setNodes, setEdges }) => {
    try {
      const response = await dispatch(fetchDiagram({ formValues: { id: diagramId } })).unwrap();
      const { data: diagramData } = response;
      const deserializedData = JSON.parse(diagramData.data);

      if (deserializedData) {
        const { nodes, edges, view, templateSettings } = deserializedData;
        const gridGapPx = typeof view?.gridGapPx === 'number' && view.gridGapPx >= 25 && view.gridGapPx <= 120
          ? view.gridGapPx
          : 25;
        const loadedNodes = alignNodesToNearestGrid(Array.isArray(nodes) ? nodes : [], gridGapPx);
        const loadedEdges = Array.isArray(edges) ? edges : [];
        deserializedData.nodes = loadedNodes;
        dispatch(setNodesActionCreator({ nodes: loadedNodes, replace: true }));
        dispatch(setEdgesActionCreator({ edges: loadedEdges }));
        setNodes(loadedNodes);
        setEdges(loadedEdges);
        if (view) {
          const { pageSize: incomingPageSize } = view;
          if (typeof incomingPageSize === 'string' && VALID_PAGE_SIZES.has(incomingPageSize)) {
            dispatch(setPageSize(incomingPageSize));
          }
          if (typeof view.scaleCm === 'number' && view.scaleCm > 0) {
            dispatch(setScaleCm(view.scaleCm));
          }
          if (typeof view.labelFontPx === 'number' && view.labelFontPx > 0) {
            dispatch(setLabelFontPx(view.labelFontPx));
          }
          if (view.symbolLabelOverrides && typeof view.symbolLabelOverrides === 'object') {
            dispatch(replaceSymbolLabelOverrides(view.symbolLabelOverrides));
          } else {
            dispatch(replaceSymbolLabelOverrides({}));
          }
          if (typeof view.gridGapPx === 'number' && view.gridGapPx >= 25 && view.gridGapPx <= 120) {
            dispatch(setGridGapPx(view.gridGapPx));
          }
          if (typeof view.autosaveIntervalMinutes === 'number' && view.autosaveIntervalMinutes > 0) {
            dispatch(setAutosaveIntervalMinutes(view.autosaveIntervalMinutes));
          }
          if (typeof view.hideParameterLabelsDefault === 'boolean') {
            dispatch(setHideParameterLabelsDefault(view.hideParameterLabelsDefault));
          }
          if (typeof view.attributeLabelSeparator === 'string') {
            dispatch(setAttributeLabelSeparator(view.attributeLabelSeparator));
          }
        }
        dispatch(replaceTemplateSettings(templateSettings));
        return deserializedData;
      }
    } catch (error) {
      console.error('Failed to load diagram data:', error);
    }
    return null;
  };

  return loadDiagramData;
};

export default useDiagramDataLoader;
